"use client"

import { useEffect, useMemo, useState } from "react"

import FlagIcon from "@/components/nango/common/components/flag-icon"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select"

import { Config } from "@/types/nango"

export type PhoneCountry =
  | "BE"
  | "CA"
  | "CH"
  | "DE"
  | "ES"
  | "FR"
  | "GB"
  | "IT"
  | "LU"
  | "NL"
  | "PT"
  | "US"

type PhoneCountryOption = {
  country: PhoneCountry
  callingCode: `+${number}`
  label: { EN: string; FR: string }
  placeholder?: { EN: string; FR: string }
  // Max digits in the national number (including a leading 0), when known.
  maxDigits?: number
}

const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = [
  {
    country: "FR",
    callingCode: "+33",
    label: { EN: "France (+33)", FR: "France (+33)" },
    placeholder: { EN: "06 12 34 56 78", FR: "06 12 34 56 78" },
    maxDigits: 10,
  },
  {
    country: "BE",
    callingCode: "+32",
    label: { EN: "Belgium (+32)", FR: "Belgique (+32)" },
  },
  {
    country: "CA",
    callingCode: "+1",
    label: { EN: "Canada (+1)", FR: "Canada (+1)" },
  },
  {
    country: "CH",
    callingCode: "+41",
    label: { EN: "Switzerland (+41)", FR: "Suisse (+41)" },
  },
  {
    country: "DE",
    callingCode: "+49",
    label: { EN: "Germany (+49)", FR: "Allemagne (+49)" },
  },
  {
    country: "ES",
    callingCode: "+34",
    label: { EN: "Spain (+34)", FR: "Espagne (+34)" },
  },
  {
    country: "GB",
    callingCode: "+44",
    label: { EN: "United Kingdom (+44)", FR: "Royaume-Uni (+44)" },
  },
  {
    country: "IT",
    callingCode: "+39",
    label: { EN: "Italy (+39)", FR: "Italie (+39)" },
  },
  {
    country: "LU",
    callingCode: "+352",
    label: { EN: "Luxembourg (+352)", FR: "Luxembourg (+352)" },
  },
  {
    country: "NL",
    callingCode: "+31",
    label: { EN: "Netherlands (+31)", FR: "Pays-Bas (+31)" },
  },
  {
    country: "PT",
    callingCode: "+351",
    label: { EN: "Portugal (+351)", FR: "Portugal (+351)" },
  },
  {
    country: "US",
    callingCode: "+1",
    label: { EN: "United States (+1)", FR: "États-Unis (+1)" },
  },
] as const

const DEFAULT_PHONE_PLACEHOLDER = "06 12 34 56 78"

function formatNationalNumber(value: string, maxDigits?: number): string {
  let digitsOnly = value.replace(/\D/g, "")
  if (maxDigits) digitsOnly = digitsOnly.slice(0, maxDigits)
  if (!digitsOnly) return ""
  return digitsOnly.match(/.{1,2}/g)?.join(" ") ?? digitsOnly
}

/**
 * Renders a stored E.164 number the way its country writes it.
 *
 * French numbers are read as five pairs starting with the trunk zero
 * ("06 12 34 56 78"), which is what the input itself shows: a raw
 * "+33612345678" is correct but nobody reads their own number that way. Other
 * countries keep their calling code, since no national convention is known for
 * them here.
 */
export function formatPhoneNumberForDisplay(value: string): string {
  if (!value) return ""

  const { country, nationalNumber } = splitE164PhoneNumber(value)
  if (!nationalNumber) return value
  if (country === "FR") return nationalNumber

  const option = PHONE_COUNTRY_OPTIONS.find((o) => o.country === country)
  return option ? `${option.callingCode} ${nationalNumber}` : value
}

function getPhoneCountryLabel(
  option: PhoneCountryOption,
  config: Config,
): string {
  return config.LOCALE === "FR" ? option.label.FR : option.label.EN
}

function buildE164PhoneNumber(
  country: PhoneCountry,
  nationalNumber: string,
): string {
  const option = PHONE_COUNTRY_OPTIONS.find((o) => o.country === country)
  if (!option) return ""

  let digitsOnly = nationalNumber.replace(/\D/g, "")
  if (option.maxDigits) digitsOnly = digitsOnly.slice(0, option.maxDigits)
  if (!digitsOnly) return ""

  const digitsWithoutLeadingZero = digitsOnly.startsWith("0")
    ? digitsOnly.slice(1)
    : digitsOnly

  return `${option.callingCode}${digitsWithoutLeadingZero}`
}

function splitE164PhoneNumber(value: string): {
  country: PhoneCountry
  nationalNumber: string
} {
  if (!value || !value.startsWith("+")) {
    return { country: "FR", nationalNumber: "" }
  }

  const match = [...PHONE_COUNTRY_OPTIONS]
    .sort((a, b) => b.callingCode.length - a.callingCode.length)
    .find((o) => value.startsWith(o.callingCode))

  if (!match) {
    return { country: "FR", nationalNumber: "" }
  }

  const remainingDigits = value
    .slice(match.callingCode.length)
    .replace(/\D/g, "")

  if (match.country === "FR") {
    return {
      country: "FR",
      nationalNumber: remainingDigits
        ? formatNationalNumber(`0${remainingDigits}`, match.maxDigits)
        : "",
    }
  }

  return {
    country: match.country,
    nationalNumber: formatNationalNumber(remainingDigits),
  }
}

export type PhoneNumberInputProps = {
  config: Config
  value: string
  onNumberChange?: (value: string) => void
  onCountryChange?: (country: PhoneCountry) => void
  disabled?: boolean
  id?: string
}

export default function PhoneNumberInput({
  config,
  value,
  onNumberChange,
  onCountryChange,
  disabled = false,
  id,
}: PhoneNumberInputProps) {
  const derived = useMemo(() => splitE164PhoneNumber(value), [value])

  const [phoneCountry, setPhoneCountry] = useState<PhoneCountry>(
    derived.country,
  )
  const [phoneNationalNumber, setPhoneNationalNumber] = useState<string>(
    derived.nationalNumber,
  )

  useEffect(() => {
    if (derived.country !== phoneCountry) setPhoneCountry(derived.country)
    if (derived.nationalNumber !== phoneNationalNumber)
      setPhoneNationalNumber(derived.nationalNumber)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derived.country, derived.nationalNumber])

  const optionsSorted = useMemo(() => {
    return [...PHONE_COUNTRY_OPTIONS].sort((a, b) => {
      if (a.country === "FR") return -1
      if (b.country === "FR") return 1
      return getPhoneCountryLabel(a, config).localeCompare(
        getPhoneCountryLabel(b, config),
        config.LOCALE,
        { sensitivity: "base" },
      )
    })
  }, [config])

  const selectedOption = useMemo(
    () => PHONE_COUNTRY_OPTIONS.find((o) => o.country === phoneCountry),
    [phoneCountry],
  )

  const maxDigits = selectedOption?.maxDigits

  const placeholder = useMemo(() => {
    if (!selectedOption?.placeholder) return DEFAULT_PHONE_PLACEHOLDER
    return config.LOCALE === "FR"
      ? selectedOption.placeholder.FR
      : selectedOption.placeholder.EN
  }, [config.LOCALE, selectedOption])

  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <Select
        value={phoneCountry}
        onValueChange={(nextValue) => {
          const nextCountry = nextValue as PhoneCountry
          setPhoneCountry(nextCountry)
          onNumberChange?.(
            buildE164PhoneNumber(nextCountry, phoneNationalNumber),
          )
          onCountryChange?.(nextCountry)
        }}
        disabled={disabled}
      >
        <SelectTrigger
          className="w-full sm:w-[112px] lg:w-[200px]"
          aria-label={
            selectedOption
              ? getPhoneCountryLabel(selectedOption, config)
              : "Country"
          }
        >
          <span className="min-w-0 truncate">
            {selectedOption ? (
              <>
                <FlagIcon iso2={selectedOption.country} className="mr-1.5" />
                {/* The country name whenever the trigger has room for it: on
                    its own line below sm, and again from lg where the field
                    stops competing with the number for width. */}
                <span className="sm:hidden lg:inline">
                  {getPhoneCountryLabel(selectedOption, config)}
                </span>
                {/* Compact form so the national number keeps most of the row
                    width. The full country name stays in the dropdown list. */}
                <span className="hidden sm:inline lg:hidden">
                  {selectedOption.callingCode}
                </span>
              </>
            ) : null}
          </span>
        </SelectTrigger>
        <SelectContent>
          {optionsSorted.map((option) => (
            <SelectItem key={option.country} value={option.country}>
              <FlagIcon iso2={option.country} className="mr-1.5" />
              {getPhoneCountryLabel(option, config)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        id={id}
        className="w-full min-w-0 sm:flex-1"
        placeholder={placeholder}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={phoneNationalNumber}
        disabled={disabled}
        onChange={(e) => {
          const nextNational = e.target.value
          setPhoneNationalNumber(formatNationalNumber(nextNational, maxDigits))
          onNumberChange?.(buildE164PhoneNumber(phoneCountry, nextNational))
        }}
      />
    </div>
  )
}
